import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Form, Formik } from 'formik';
import { object, string } from 'yup';
import graphql from 'babel-plugin-relay/macro';

import Stack from '@mui/material/Stack';

import TextField from 'components/TextField';
import Button from 'components/Button';

import { UserProfile_user$key } from '__generated__/UserProfile_user.graphql';
import { UserProfileMutation } from '__generated__/UserProfileMutation.graphql';
import { useNotification } from 'components/Notifications';

type FormFields = {
  name: string;
  email: string;
  itemsPerPage: string;
};

const validation = object().shape({
  name: string().required('Prašome įvesti vardą'),
  email: string()
    .required('El. paštas privalomas')
    .email('Neteisingas el. pašto formatas'),
});

type Props = {
  user: UserProfile_user$key;
};

const UserProfile: FC<Props> = ({ user }) => {
  const { pop } = useNotification();

  const { id, name, email, itemsPerPage } = useFragment(
    graphql`
      fragment UserProfile_user on User {
        id
        name
        email
        itemsPerPage
      }
    `,
    user
  );

  const [commit, loading] = useMutation<UserProfileMutation>(graphql`
    mutation UserProfileMutation($id: ID!, $input: UpdateUserInput!) {
      updateUser(id: $id, input: $input) {
        id
        itemsPerPage
      }
    }
  `);

  const initial = {
    name,
    email,
    itemsPerPage: itemsPerPage ? itemsPerPage.toString() : '',
  };

  const handleSubmit = useCallback(
    ({ name, email, itemsPerPage }: FormFields) => {
      commit({
        variables: {
          id,
          input: {
            name,
            email,
            itemsPerPage: itemsPerPage.length > 0 ? Number(itemsPerPage) : null,
          },
        },

        onCompleted: ({ updateUser }, errors) => {
          if (errors) {
            pop(errors[0]?.message ?? 'Nepavyko išsaugoti');
            return;
          }

          if (updateUser.itemsPerPage) {
            localStorage.setItem(
              'itemsPerPage',
              updateUser.itemsPerPage.toString()
            );
          }

          pop('Vartotojo duomenys išsaugoti');
        },
      });
    },
    [commit, id, pop]
  );

  return (
    <Formik<FormFields>
      initialValues={initial}
      onSubmit={handleSubmit}
      validationSchema={validation}>
      <Form>
        <Stack spacing={4}>
          <TextField name="name" label="Vardas" />
          <TextField name="email" label="El. pašto adresas" />
          <TextField name="itemsPerPage" label="Įrašų skaičius puslapyje" />

          <Button loading={loading} type="submit">
            Saugoti
          </Button>
        </Stack>
      </Form>
    </Formik>
  );
};

export default UserProfile;
